import React, { useEffect, useMemo, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../../lib/supabase.js';
import { formatDateTime, formatUF } from '../../../styles/tokens/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Input } from '../../../components/ui/input';
import { Sheet, SheetContent, SheetClose } from '../../../components/ui/sheet';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import SectionHeader from '../../../components/ui/fancy/SectionHeader';
import DashboardFilters from '../../../components/ui/fancy/DashboardFilters';
import { KpiGrid } from '../../../components/ui/KpiCard';
import GlobalSearch from '../../../components/ui/fancy/GlobalSearch';
import { useNavigate } from 'react-router-dom';
import { subDays } from 'date-fns';

const safeParseResumen = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch { return {}; }
};

const calcEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  const hoy = new Date();
  const nac = new Date(fechaNacimiento);
  if (Number.isNaN(nac.getTime())) return null;
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad;
};

const CargasModal = ({ cargas, onClose }) => {
  if (!cargas || cargas.length === 0) return null;

  return (
    <Sheet open={!!cargas} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-white dark:bg-gray-900">
        {/* Header Sticky */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Cargas Familiares
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {cargas.length} {cargas.length === 1 ? 'dependiente' : 'dependientes'}
            </p>
          </div>
          <SheetClose className="rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-800 p-2">
            <X className="h-5 w-5" />
          </SheetClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col gap-5">
            {cargas.map((carga, idx) => (
              <div
                key={carga.id || idx}
                className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="grid grid-cols-2 gap-5">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Tipo
                    </span>
                    <span className="text-base font-semibold text-gray-900 dark:text-white capitalize">
                      {carga.tipo || '—'}
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Sexo
                    </span>
                    <span className="text-base font-semibold text-gray-900 dark:text-white">
                      {carga.sexo || '—'}
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Previsión
                    </span>
                    <span className="text-base font-semibold text-blue-600 dark:text-blue-400 capitalize">
                      {carga.prevision || '—'}
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Edad
                    </span>
                    <span className="text-base font-bold text-[var(--yool-success)] dark:text-green-400">
                      {calcEdad(carga.fechaNacimiento) != null
                        ? `${calcEdad(carga.fechaNacimiento)} años`
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const SimulacionRow = ({ sim }) => {
  const resumen = safeParseResumen(sim.resumen);
  const primaBase = resumen?.primaCalculada?.primabase || {};
  const primaTotal = resumen?.primaCalculada?.primatotal?.total;
  const primaTitular = primaBase?.titular;
  const primaCargas = Array.isArray(primaBase?.cargas) ? primaBase.cargas : [];
  const cargas = Array.isArray(resumen?.cargasFamiliares) ? resumen.cargasFamiliares : [];

  const totalPrimasCargas = primaCargas.reduce((sum, c) => sum + (c.primaUF || 0), 0);

  return (
    <div className="py-3 px-4 rounded-md bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 text-sm">
      <div className="grid grid-cols-12 gap-4 items-center">
        {/* Producto y Plan */}
        <div className="col-span-5 flex flex-col gap-1">
          <div className="font-semibold text-gray-900 dark:text-white">
            {sim.producto_nombre || 'N/A'}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">{sim.plan_nombre || 'N/A'}</span>
            <span className="font-mono text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded">
              {sim.codigo}
            </span>
          </div>
        </div>

        {/* Precios */}
        <div className="col-span-7 grid grid-cols-3 gap-4">
          <div className="flex flex-col items-end">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Titular</span>
            <span className="text-base font-semibold text-blue-600 dark:text-blue-400">
              {primaTitular != null ? formatUF(primaTitular, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
            </span>
          </div>

          {cargas.length > 0 && totalPrimasCargas > 0 && (
            <div className="flex flex-col items-end">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Cargas</span>
              <span className="text-base font-semibold text-amber-600 dark:text-amber-400">
                {formatUF(totalPrimasCargas, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          <div className="flex flex-col items-end">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Total</span>
            <span className="text-base font-bold text-[var(--yool-success)] dark:text-green-400">
              {primaTotal != null ? formatUF(primaTotal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const getEstado = (sims) => {
  if (!sims?.length) return null;
  const allExpired = sims.every((s) => {
    if (!s.expires_at) return false;
    const d = new Date(s.expires_at);
    return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
  });
  return allExpired ? 'expirada' : 'vigente';
};

const SessionRow = ({ sesion }) => {
  const [expanded, setExpanded] = useState(false);
  const [cargasModal, setCargasModal] = useState(null);
  const sims = sesion.simulaciones;
  const firstSim = sims[0];
  const resumen0 = safeParseResumen(firstSim?.resumen);
  const titular0 = resumen0?.datosTitular || {};
  const edad0 = calcEdad(titular0?.fechaNacimiento);
  const cargas0 = Array.isArray(resumen0?.cargasFamiliares) ? resumen0.cargasFamiliares : [];
  const estado = getEstado(sims);

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded((v) => !v)}
      >
        <TableCell className="w-6">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell className="whitespace-nowrap text-sm">
          {formatDateTime(sesion.fecha)}
        </TableCell>
        <TableCell className="text-sm py-4 px-4">
          <div className="inline-flex items-center justify-center rounded px-2.5 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
            {sims.length}
          </div>
        </TableCell>
        <TableCell className="text-sm">
          <div>
            {edad0 != null ? `${edad0} años` : 'N/A'}
            {titular0.sexo ? ` · ${titular0.sexo}` : ''}
            {titular0.prevision ? ` · ${titular0.prevision}` : ''}
          </div>
        </TableCell>
        <TableCell>
          {cargas0.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCargasModal(cargas0);
              }}
              className="inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors"
            >
              {cargas0.length} carga{cargas0.length > 1 ? 's' : ''}
            </button>
          )}
        </TableCell>
        <TableCell>
          <span className={`text-xs font-semibold ${estado === 'vigente' ? 'text-[var(--yool-success)]' : 'text-red-500'}`}>
            {estado === 'vigente' ? 'Vigente' : estado === 'expirada' ? 'Expirada' : '—'}
          </span>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground font-mono">
          {sesion.sesion_id?.slice(0, 8)}…
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="cursor-default">
          <TableCell colSpan={7} className="bg-gradient-to-b from-blue-50/30 to-transparent dark:from-blue-900/10 dark:to-transparent px-6 py-4 select-none">
            <div className="flex flex-col gap-3 select-none pointer-events-none">
              {sims.map((sim) => (
                <SimulacionRow key={sim.id} sim={sim} />
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
      {cargasModal && <CargasModal cargas={cargasModal} onClose={() => setCargasModal(null)} />}
    </>
  );
};

const ConversionesSimulaciones = () => {
  const { session } = useSession();
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [growthData, setGrowthData] = useState({ total: 0, lastPeriod: 0, percent: 0 });
  const [last30DaysCount, setLast30DaysCount] = useState(0);

  useEffect(() => {
    if (!session) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const from = filters.dateRange?.from ?? subDays(now, 30);
        const to = filters.dateRange?.to ?? now;

        const diff = to.getTime() - from.getTime();
        const prevFrom = new Date(from.getTime() - diff);
        const prevTo = new Date(from.getTime() - 1);

        const [currentRes, prevRes, listRes] = await Promise.all([
          supabase.from('company_cotizaciones')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', from.toISOString())
            .lte('created_at', to.toISOString()),
          supabase.from('company_cotizaciones')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', prevFrom.toISOString())
            .lte('created_at', prevTo.toISOString()),
          supabase.from('company_cotizaciones')
            .select('id,sesion_id,created_at,expires_at,compania_nombre,producto_nombre,plan_nombre,codigo,resumen')
            .gte('created_at', from.toISOString())
            .lte('created_at', to.toISOString())
            .order('created_at', { ascending: false })
            .limit(500),
        ]);

        if (listRes.error) throw listRes.error;

        const currentCount = currentRes.count || 0;
        const prevCount = prevRes.count || 0;
        setLast30DaysCount(currentCount);
        setGrowthData({
          total: currentCount,
          lastPeriod: prevCount,
          percent: prevCount > 0 ? ((currentCount - prevCount) / prevCount) * 100 : 0,
        });

        const bySession = {};
        for (const row of listRes.data || []) {
          const key = row.sesion_id || '__sin_sesion__';
          if (!bySession[key]) {
            bySession[key] = { sesion_id: key, fecha: row.created_at, simulaciones: [] };
          }
          bySession[key].simulaciones.push(row);
          if (new Date(row.created_at) > new Date(bySession[key].fecha)) {
            bySession[key].fecha = row.created_at;
          }
        }

        setRows(Object.values(bySession).sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
      } catch (e) {
        setError(e?.message || 'Error cargando datos');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [session, supabase, filters]);

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((sesion) =>
      sesion.sesion_id?.toLowerCase().includes(q) ||
      sesion.simulaciones.some(
        (s) =>
          s.codigo?.toLowerCase().includes(q) ||
          s.producto_nombre?.toLowerCase().includes(q) ||
          s.plan_nombre?.toLowerCase().includes(q)
      )
    );
  }, [rows, searchQuery]);

  const kpiItems = [
    {
      title: 'Simulaciones en rango',
      value: last30DaysCount,
      helper: 'Total de cotizaciones',
      trend: 'neutral',
    },
    {
      title: 'Sesiones únicas',
      value: rows.length,
      helper: 'Sesiones con al menos 1 simulación',
      trend: 'neutral',
    },
    {
      title: 'Periodo anterior',
      value: growthData.lastPeriod,
      helper: 'Comparación equivalente',
      trend: growthData.percent >= 0 ? 'up' : 'down',
      delta: `${growthData.percent >= 0 ? '+' : ''}${Math.abs(growthData.percent).toFixed(1)}% vs periodo actual`,
    },
  ];

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Conversiones - Simulaciones"
        subtitle="Agrupadas por sesión"
        action={<GlobalSearch onNavigate={(p) => navigate(p)} />}
      />

      <DashboardFilters
        onFilterChange={setFilters}
        showClientFilter={false}
        channels={[
          { value: 'web', label: 'Web Directa' },
          { value: 'fb', label: 'Facebook Ads' },
        ]}
      />

      <KpiGrid items={kpiItems} loading={loading} />

      <Card className="border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-sm">
        <CardHeader className="flex flex-col md:flex-row items-center justify-between gap-4">
          <CardTitle className="text-lg font-semibold">Sesiones</CardTitle>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar código, producto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground py-4">Cargando...</div>
          ) : error ? (
            <div className="text-sm text-[var(--yool-error)] py-4">{error}</div>
          ) : filteredRows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">Sin registros</div>
          ) : (
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-6" />
                    <TableHead>Fecha sesión</TableHead>
                    <TableHead>Simulaciones</TableHead>
                    <TableHead>Perfil titular</TableHead>
                    <TableHead>Cargas</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>ID Sesión</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((sesion) => (
                    <SessionRow key={sesion.sesion_id} sesion={sesion} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConversionesSimulaciones;
